import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import store from 'store';

import CloseIcon from '@mui/icons-material/Close';

const isNotificationSupported = () => typeof window !== 'undefined' && 'Notification' in window;
const isStandalone = () =>
	typeof window !== 'undefined' &&
	(window.matchMedia?.('(display-mode: standalone)').matches ||
		(window.navigator as Navigator & { standalone?: boolean }).standalone === true);
const dismissKey = 'push-permission-prompt-dismissed';

function PushNotificationsManager(): React.ReactElement | null {
	const { auth, push } = store;
	const { isAuthorized } = auth;
	const profileReady =
		Boolean(store.profile?.id) ||
		Boolean(store.profile?.student?.id) ||
		Boolean(store.profile?.parent);

	const [permission, setPermission] = useState<NotificationPermission>(() => {
		if (!isNotificationSupported()) return 'denied';
		return Notification.permission;
	});
	const [isDismissed, setIsDismissed] = useState(() => {
		if (typeof window === 'undefined') return false;
		return sessionStorage.getItem(dismissKey) === '1';
	});

	const requestedRef = useRef(false);
	const lastAuthStateRef = useRef<boolean | null>(null);
	const lastPermissionRef = useRef(permission);

	const shouldShowPrompt = permission === 'denied' && !isDismissed;
	const shouldAutoRequest = permission === 'default' && isNotificationSupported() && isStandalone();

	useEffect(() => {
		if (!shouldAutoRequest || requestedRef.current) return undefined;
		let cancelled = false;

		const handleGesture = () => {
			if (cancelled) return;
			window.removeEventListener('pointerup', handleGesture);
			window.removeEventListener('keydown', handleGesture);
			requestedRef.current = true;
			push
				.requestPermissions()
				.then((result) => {
					if (cancelled) return;
					const next = result ?? Notification.permission;
					setPermission(next);
				})
				.catch(() => {
					if (cancelled) return;
					setPermission(Notification.permission);
				});
		};

		const cleanup = () => {
			window.removeEventListener('pointerup', handleGesture);
			window.removeEventListener('keydown', handleGesture);
		};

		window.addEventListener('pointerup', handleGesture, { once: true });
		window.addEventListener('keydown', handleGesture, { once: true });

		return () => {
			cancelled = true;
			cleanup();
		};
	}, [shouldAutoRequest, push]);

	useEffect(() => {
		if (!isNotificationSupported()) return undefined;
		const syncPermission = () => setPermission(Notification.permission);
		document.addEventListener('visibilitychange', syncPermission);
		return () => document.removeEventListener('visibilitychange', syncPermission);
	}, []);

	useEffect(() => {
		if (!isAuthorized || !profileReady) return;
		push.syncTokenIfNeeded({ reason: 'profile' });
	}, [isAuthorized, profileReady, push]);

	useEffect(() => {
		if (isAuthorized && !lastAuthStateRef.current) {
			push.syncTokenIfNeeded({ reason: 'login' });
		}
		lastAuthStateRef.current = isAuthorized;
	}, [isAuthorized, push]);

	useEffect(() => {
		if (isAuthorized && profileReady) {
			if (permission === 'granted' && lastPermissionRef.current !== 'granted') {
				push.syncTokenIfNeeded({ reason: 'permission' });
			}
		}
		lastPermissionRef.current = permission;
	}, [permission, isAuthorized, profileReady, push]);

	if (!isNotificationSupported() || !shouldShowPrompt) return null;

	const handleEnable = async () => {
		const result = await push.requestPermissions();
		const next = result ?? Notification.permission;
		setPermission(next);
	};

	const handleDismiss = () => {
		setIsDismissed(true);
		sessionStorage.setItem(dismissKey, '1');
	};

	return (
		<Paper
			elevation={3}
			sx={{
				position: 'fixed',
				bottom: 16,
				left: 16,
				p: 2,
				pr: 5,
				zIndex: 1300,
				maxWidth: 360,
				border: '1px solid',
				borderColor: 'divider',
			}}
		>
			<IconButton
				aria-label="Close"
				onClick={handleDismiss}
				size="small"
				sx={{ position: 'absolute', top: 8, right: 8 }}
			>
				<CloseIcon fontSize="small" />
			</IconButton>
			<Stack spacing={1}>
				<Typography variant="subtitle1">Enable push notifications</Typography>
				<Typography variant="body2" color="text.secondary">
					{permission === 'denied'
						? 'Notifications are blocked in the browser or device settings. Please enable them to receive updates.'
						: 'Stay up to date with alerts and messages.'}
				</Typography>
				{permission !== 'denied' && (
					<Stack direction="row" spacing={1}>
						<Button variant="contained" onClick={handleEnable} sx={{ flexGrow: 1 }}>
							Enable
						</Button>
						<Button variant="text" onClick={() => setPermission(Notification.permission)}>
							Later
						</Button>
					</Stack>
				)}
			</Stack>
		</Paper>
	);
}

export default observer(PushNotificationsManager);
